import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@saas-kit/lib';
import { Button, ConfirmDialog, ErrorBanner, PageHeader } from '@saas-kit/ui';

import { TenantRequiredState } from '../../components/TenantRequiredState';
import { CounterPicker } from './CounterPicker';
import { useWorkstation } from './useWorkstation';
import { WorkstationView } from './WorkstationView';

export function QmsWorkstationPage(): JSX.Element {
  const { t } = useTranslation('qms');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const ws = useWorkstation();
  const [confirmReset, setConfirmReset] = useState(false);

  if (!selectedTenant) {
    return (
      <TenantRequiredState
        title={t('tenant_required.title')}
        body={t('tenant_required.body')}
        cta={t('tenant_required.cta')}
      />
    );
  }
  if (!ws.canView) {
    return <ErrorBanner>{t('errors.workstation_denied')}</ErrorBanner>;
  }

  const counterName = ws.workstation?.counter.name ?? '';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title={t('workstation.title')}
          description={ws.session ? counterName : t('workstation.subtitle')}
        />
        <div className="flex items-center gap-2">
          {ws.canResetDay ? (
            <Button
              variant="secondary"
              disabled={ws.reset.isPending}
              onClick={() => setConfirmReset(true)}
            >
              {t('workstation.actions.reset_day')}
            </Button>
          ) : null}
          {ws.session ? (
            <Button
              variant="destructive"
              disabled={ws.release.isPending}
              onClick={() => ws.release.mutate()}
            >
              {t('workstation.actions.end_shift')}
            </Button>
          ) : null}
        </div>
      </div>

      {!ws.canClaim ? (
        <ErrorBanner>{t('errors.workstation_denied')}</ErrorBanner>
      ) : ws.session ? (
        <WorkstationView ws={ws} />
      ) : (
        <CounterPicker ws={ws} />
      )}

      {confirmReset ? (
        <ConfirmDialog
          title={t('workstation.reset.title')}
          body={t('workstation.reset.body')}
          confirmLabel={t('workstation.reset.confirm')}
          cancelLabel={t('confirm.cancel')}
          variant="destructive"
          isBusy={ws.reset.isPending}
          onCancel={() => setConfirmReset(false)}
          onConfirm={() => {
            ws.reset.mutate();
            setConfirmReset(false);
          }}
        />
      ) : null}
    </div>
  );
}
